import { useState, useCallback } from 'react';
import { useApp } from '../../../context/AppContext';
import {
    checkEligibility as apiCheckEligibility,
    generateCreditCode as apiGenerateCreditCode,
    fetchCreditCodes as apiFetchCreditCodes,
    fetchCreditStats as apiFetchCreditStats,
    updateCodeStatus as apiUpdateCodeStatus,
    deleteCode as apiDeleteCode,
    claimCreditCode as apiClaimCreditCode,
    getCodeById as apiGetCodeById,
    getAuthHeaders
} from '../../../api/creditCodes';
import logger from '../../../utils/logger';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
};

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES');
};

/**
 * Hook personalizado para gestionar códigos de crédito
 * Maneja la lógica completa de generación, gestión y reclamo
 */
export const useCreditCodes = () => {
    const { notify } = useApp();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [codes, setCodes] = useState([]);
    const [currentCode, setCurrentCode] = useState(null);
    const [stats, setStats] = useState(null);
    const [eligibility, setEligibility] = useState(null);

    const handleError = useCallback((err, defaultMessage = 'Error en la operación') => {
        const errorMessage = err?.response?.data?.error || err?.message || defaultMessage;
        logger.error('[CreditCodes]', err);
        setError(errorMessage);
        notify({
            type: 'error',
            title: 'Error',
            text: errorMessage
        });
        throw err;
    }, [notify]);

    const handleSuccess = useCallback((message = 'Operación exitosa') => {
        setError(null);
        notify({
            type: 'success',
            title: 'Éxito',
            text: message
        });
    }, [notify]);

    // ==================== UTILIDADES (definidas antes de su uso) ====================

    const getStatusLabel = useCallback((status) => {
        const labels = {
            active: 'Activo',
            expired: 'Expirado',
            disabled: 'Desactivado'
        };
        return labels[status] || status;
    }, []);

    const getStatusColor = useCallback((status) => {
        const colors = {
            active: '#10b981',
            expired: '#f59e0b',
            disabled: '#ef4444'
        };
        return colors[status] || '#6b7280';
    }, []);

    // ==================== GENERACIÓN DE CÓDIGOS ====================

    const generateCode = useCallback(async (employerId, credits, options = {}) => {
        if (!employerId || !credits) {
            throw new Error('Empleador y créditos son requeridos');
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await apiGenerateCreditCode(employerId, credits, options);
            handleSuccess('Código generado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al generar código de crédito');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== GESTIÓN ADMINISTRATIVA ====================

    const getAllCodes = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await apiFetchCreditCodes(filters);
            const codesList = Array.isArray(data) ? data : (data?.codes || []);

            // Formatear datos para la tabla
            const formattedCodes = codesList.map(code => ({
                ...code,
                createdAtFormatted: formatDateTime(code.createdAt),
                expiresAtFormatted: formatDate(code.expiresAt),
                usedCountDisplay: `${code.usedCount}/${code.maxUses}`,
                statusDisplay: getStatusLabel(code.status),
                assignedToName: code.assignedTo?.name || 'N/A'
            }));

            setCodes(formattedCodes);
            return formattedCodes;
        } catch (err) {
            handleError(err, 'Error al obtener códigos');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError, getStatusLabel]);

    const getCodeById = useCallback(async (code) => {
        if (!code) return null;

        setIsLoading(true);
        setError(null);

        try {
            const data = await apiGetCodeById(code);
            setCurrentCode(data);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener código');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getEmployerCodes = useCallback(async (employerId) => {
        if (!employerId) return [];

        setIsLoading(true);
        setError(null);

        try {
            // TODO: Implementar función para obtener códigos de empleador
            const data = await fetch(`https://talentos-services.xn--am-yja.org/api/credit-codes/employer/${employerId}`, {
                headers: getAuthHeaders()
            }).then(res => res.json());
            const codesList = Array.isArray(data) ? data : (data?.codes || []);
            return codesList;
        } catch (err) {
            handleError(err, 'Error al obtener códigos del empleador');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const updateCode = useCallback(async (code, data) => {
        if (!code) {
            throw new Error('Código requerido');
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await apiUpdateCodeStatus(code, data.status);
            handleSuccess('Código actualizado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar código');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const deleteCode = useCallback(async (code) => {
        if (!code) {
            throw new Error('Código requerido');
        }

        setIsLoading(true);
        setError(null);

        try {
            await apiDeleteCode(code);
            handleSuccess('Código desactivado exitosamente');
            return true;
        } catch (err) {
            handleError(err, 'Error al desactivar código');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const expireCode = useCallback(async (code) => {
        if (!code) {
            throw new Error('Código requerido');
        }

        setIsLoading(true);
        setError(null);

        try {
            await apiUpdateCodeStatus(code, 'expired');
            handleSuccess('Código expirado exitosamente');
            return true;
        } catch (err) {
            handleError(err, 'Error al expirar código');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== RECLAMO DE CÓDIGOS ====================

    const claimCode = useCallback(async (code) => {
        if (!code) {
            throw new Error('Código requerido');
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await apiClaimCreditCode(code);
            handleSuccess(`${result.credits} créditos agregados a tu cuenta`);
            return result;
        } catch (err) {
            handleError(err, 'Error al reclamar código');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== CONSULTAS Y ESTADÍSTICAS ====================

    const checkEligibility = useCallback(async (userId) => {
        if (!userId) return null;

        setIsLoading(true);
        setError(null);

        try {
            const data = await apiCheckEligibility(userId);
            setEligibility(data);
            return data;
        } catch (err) {
            handleError(err, 'Error al verificar elegibilidad');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getStats = useCallback(async (period = 'month') => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await apiFetchCreditStats(period);
            setStats(data);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener estadísticas');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getStatisticsByCreator = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);

        try {
            // Obtener todos los códigos (sin límite para estadísticas)
            const result = await apiFetchCreditCodes({ limit: 10000, ...filters });
            const allCodes = result.codes || result || [];

            // Filtrar por rango de fechas si se proporciona
            let codes = allCodes;
            if (filters.startDate || filters.endDate) {
                codes = allCodes.filter(code => {
                    if (!code.createdAt) return false;
                    const codeDate = new Date(code.createdAt);
                    const startDate = filters.startDate ? new Date(filters.startDate) : null;
                    const endDate = filters.endDate ? new Date(filters.endDate) : null;
                    
                    if (startDate && codeDate < startDate) return false;
                    if (endDate) {
                        // Incluir el día completo de endDate
                        const endDateEnd = new Date(endDate);
                        endDateEnd.setHours(23, 59, 59, 999);
                        if (codeDate > endDateEnd) return false;
                    }
                    return true;
                });
            }

            // Agrupar por createdBy
            const groupedByCreator = {};
            codes.forEach(code => {
                const creatorId = code.createdBy;
                if (!creatorId) return;

                if (!groupedByCreator[creatorId]) {
                    groupedByCreator[creatorId] = {
                        creatorId,
                        totalCodes: 0,
                        activeCodes: 0,
                        expiredCodes: 0,
                        disabledCodes: 0,
                        totalCredits: 0,
                        codes: [],
                        firstCodeDate: null,
                        lastCodeDate: null
                    };
                }

                const group = groupedByCreator[creatorId];
                group.totalCodes++;
                group.totalCredits += (code.credits || 0);
                group.codes.push(code);

                // Contar por estado
                if (code.status === 'active') group.activeCodes++;
                else if (code.status === 'expired') group.expiredCodes++;
                else if (code.status === 'disabled') group.disabledCodes++;

                // Fechas
                if (code.createdAt) {
                    const codeDate = new Date(code.createdAt);
                    if (!group.firstCodeDate || codeDate < new Date(group.firstCodeDate)) {
                        group.firstCodeDate = code.createdAt;
                    }
                    if (!group.lastCodeDate || codeDate > new Date(group.lastCodeDate)) {
                        group.lastCodeDate = code.createdAt;
                    }
                }
            });

            // Convertir a array y ordenar por total de códigos
            const statistics = Object.values(groupedByCreator)
                .sort((a, b) => b.totalCodes - a.totalCodes);

            return statistics;
        } catch (err) {
            handleError(err, 'Error al obtener estadísticas por creador');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);



    // ==================== UTILIDADES ====================

    const copyToClipboard = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            handleSuccess('Enlace copiado al portapapeles');
        } catch (err) {
            handleError(err, 'No se pudo copiar al portapapeles');
        }
    }, [handleError, handleSuccess]);

    // ==================== COLUMNAS PARA TABLA ====================

    const getTableColumns = useCallback(() => [
        { key: 'code', label: 'Código', style: { fontFamily: 'monospace' } },
        { key: 'credits', label: 'Créditos' },
        { key: 'assignedToName', label: 'Empleador' },
        { key: 'statusDisplay', label: 'Estado' },
        { key: 'usedCountDisplay', label: 'Usos' },
        { key: 'expiresAtFormatted', label: 'Expira' },
        { key: 'actions', label: 'Acciones' }
    ], []);

    const getTableCustomElements = useCallback((onViewCode) => ({
        statusDisplay: (row) => (
            <span style={{
                color: getStatusColor(row.status),
                fontWeight: 600
            }}>
                {row.statusDisplay}
            </span>
        ),
        code: (row) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <code style={{
                    background: '#f3f4f6',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                }}>
                    {row.code}
                </code>
                <button
                    onClick={() => {
                        const claimLink = row.claimLink || (typeof window !== 'undefined' ? `${window.location.origin}/claim/${row.code}` : row.code);
                        copyToClipboard(claimLink);
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        color: '#6b7280'
                    }}
                    title="Copiar URL completa"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.33333 2.66667H11.3333C12.0697 2.66667 12.6667 3.26362 12.6667 4V10.6667C12.6667 11.403 12.0697 12 11.3333 12H5.33333C4.59695 12 4 11.403 4 10.6667V4C4 3.26362 4.59695 2.66667 5.33333 2.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.6667 2.66667V1.33333C10.6667 0.596954 10.0697 0 9.33333 0H2.66667C1.93029 0 1.33333 0.596954 1.33333 1.33333V8C1.33333 8.73638 1.93029 9.33333 2.66667 9.33333H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        ),
        actions: (row) => (
            <div style={{ display: 'flex', gap: '8px' }}>
                <button
                    onClick={() => {
                        if (onViewCode) {
                            onViewCode(row.code);
                        } else {
                            // Fallback: navegar directamente si no se pasa función
                            window.location.href = `/talentos/credit-codes/${row.code}`;
                        }
                    }}
                    style={{
                        padding: '4px 8px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Ver
                </button>
                {row.status === 'active' && (
                    <button
                        onClick={() => expireCode(row.code)}
                        style={{
                            padding: '4px 8px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Expirar
                    </button>
                )}
                <button
                    onClick={() => deleteCode(row.code)}
                    style={{
                        padding: '4px 8px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Desactivar
                </button>
            </div>
        )
    }), [getStatusColor, copyToClipboard, expireCode, deleteCode]);

    return {
        // Estado
        isLoading,
        error,
        codes,
        currentCode,
        stats,
        eligibility,

        // Generación
        generateCode,

        // Gestión administrativa
        getAllCodes,
        getCodeById,
        getEmployerCodes,
        updateCode,
        deleteCode,
        expireCode,

        // Reclamo
        claimCode,

        // Consultas
        checkEligibility,
        getStats,
        getStatisticsByCreator,

        // Utilidades
        copyToClipboard,
        getStatusLabel,
        getStatusColor,

        // UI Helpers
        getTableColumns,
        getTableCustomElements,

        // Reset
        reset: () => {
            setError(null);
            setCurrentCode(null);
            setEligibility(null);
        }
    };
};

export default useCreditCodes;
